import React, { useState, useEffect, useCallback } from 'react';
import { PageContentWrapper, PageHeader, PageDescription } from '../main/LayoutStyles';
import {
    BookingLayout, CalendarSection, TimeSlotsSection, TimeSlotButton,
    ConfirmationSection, ConfirmBookingButton, PsychologistSelector
} from './AppointmentBookingStyles';
import { AlertBox } from '../main/CommonStyles';
import apiClient from '../services/api';
import { Loader2, AlertTriangle, UserCheck, CalendarDays, Clock, Video, Building } from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const AppointmentBookingPage = () => {
    const [psychologists, setPsychologists] = useState([]);
    const [selectedPsychologistId, setSelectedPsychologistId] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [notes, setNotes] = useState('');

    const [loadingPsychologists, setLoadingPsychologists] = useState(true);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [booking, setBooking] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchPsychologists = async () => {
            try {
                const response = await apiClient.get('/psychologists');
                setPsychologists(response.data);
                if (response.data.length > 0) {
                    setSelectedPsychologistId(response.data[0].id);
                }
            } catch (err) {
                setError('Nie udało się pobrać listy specjalistów.');
            } finally {
                setLoadingPsychologists(false);
            }
        };
        fetchPsychologists();
    }, []);

    const fetchAvailableSlots = useCallback(async (psychologistId, date) => {
        if (!psychologistId || !date) return;
        setLoadingSlots(true);
        setError('');
        setAvailableSlots([]);
        setSelectedSlot(null);
        try {
            const dateString = formatDateToYYYYMMDD(date);
            const response = await apiClient.get(`/appointments/availability/${psychologistId}?date=${dateString}`);
            setAvailableSlots(response.data);
        } catch (err) {
            setError('Nie udało się pobrać dostępnych terminów dla tego dnia.');
        } finally {
            setLoadingSlots(false);
        }
    }, []);

    useEffect(() => {
        fetchAvailableSlots(selectedPsychologistId, selectedDate);
    }, [selectedPsychologistId, selectedDate, fetchAvailableSlots]);

    const handleBookingConfirm = async () => {
        if (!selectedSlot) {
            setError('Proszę wybrać godzinę wizyty.');
            return;
        }
        setBooking(true);
        setError('');
        setSuccess('');
        try {
            await apiClient.post('/appointments/request', {
                availabilityId: selectedSlot.id,
                notes: notes,
            });
            setSuccess('Twoja prośba o rezerwację została wysłana! Otrzymasz e-mail z potwierdzeniem, gdy specjalista zaakceptuje wizytę.');
            setSelectedSlot(null);
            setNotes('');
            fetchAvailableSlots(selectedPsychologistId, selectedDate);
        } catch (err) {
            setError(err.response?.data?.message || 'Rezerwacja nie powiodła się.');
        } finally {
            setBooking(false);
        }
    };

    const tileDisabled = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date < today;
        }
        return false;
    };

    return (
        <PageContentWrapper className="custom-scrollbar">
            <PageHeader>Umów Wizytę</PageHeader>
            <PageDescription>Wybierz specjalistę, dogodny termin i zarezerwuj wizytę online.</PageDescription>

            {error && <AlertBox type="error" style={{marginBottom: '1rem'}}><AlertTriangle/>{error}</AlertBox>}
            {success && <AlertBox type="success" style={{marginBottom: '1rem'}}>{success}</AlertBox>}

            {loadingPsychologists ? <Loader2 className="icon-spin"/> : (
                <PsychologistSelector>
                    <label htmlFor="psychologist-select">Wybierz specjalistę:</label>
                    <select
                        id="psychologist-select"
                        value={selectedPsychologistId}
                        onChange={(e) => setSelectedPsychologistId(e.target.value)}
                    >
                        {psychologists.map(p => (
                            <option key={p.id} value={p.id}>{p.firstName} {p.lastName} ({p.professionalTitle})</option>
                        ))}
                    </select>
                </PsychologistSelector>
            )}

            <BookingLayout>
                {/* Kolumna 1: Kalendarz */}
                <CalendarSection>
                    <h3><CalendarDays size={20} style={{ marginRight: '8px' }} /> Wybierz Datę</h3>
                    <Calendar
                        onChange={setSelectedDate}
                        value={selectedDate}
                        locale="pl-PL"
                        tileDisabled={tileDisabled}
                    />
                </CalendarSection>

                {/* Kolumna 2: Dostępne godziny */}
                <TimeSlotsSection>
                    <h3><Clock size={20} style={{ marginRight: '8px' }} /> Dostępne Godziny</h3>
                    {loadingSlots ? <Loader2 className="icon-spin" style={{margin: '2rem auto', display: 'block'}} /> : (
                        availableSlots.length > 0 ? (
                            availableSlots.map(slot => (
                                <TimeSlotButton
                                    key={slot.id}
                                    onClick={() => setSelectedSlot(slot)}
                                    $selected={selectedSlot?.id === slot.id}
                                >
                                    <span>{new Date(slot.startTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</span>
                                    <span className="slot-type-icon" title={slot.appointmentType === 'ONLINE' ? 'Wizyta online' : 'Wizyta stacjonarna'}>
                                        {slot.appointmentType === 'ONLINE' ? <Video size={18}/> : <Building size={18}/>}
                                    </span>
                                </TimeSlotButton>
                            ))
                        ) : <p className="no-slots">Brak dostępnych terminów.</p>
                    )}
                </TimeSlotsSection>

                {/* Kolumna 3: Potwierdzenie */}
                <ConfirmationSection style={{ visibility: selectedSlot ? 'visible' : 'hidden' }}>
                    <h3><UserCheck size={20} style={{ marginRight: '8px' }} /> Rezerwacja</h3>
                    <p>
                        Termin: <strong>{selectedSlot && new Date(selectedSlot.startTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</strong>
                    </p>
                    <textarea
                        placeholder="Dodaj notatkę dla specjalisty (opcjonalnie)..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                    <ConfirmBookingButton onClick={handleBookingConfirm} disabled={booking}>
                        {booking ? <Loader2 className="icon-spin"/> : 'Zarezerwuj Termin'}
                    </ConfirmBookingButton>
                </ConfirmationSection>
            </BookingLayout>

        </PageContentWrapper>
    );
};

export default AppointmentBookingPage;